/**
 * Slice 54 — Check unit, one section per room in walk order.
 * Each room: cover photo, the cleaner's walk photos for that room, then only that room's unit items
 * with Here / Missing / Broken. Missing or Broken adds a problem to the report; switching back to
 * Here clears it straight away. Works while the job is live and after it finishes.
 */
import { useMemo, useState } from "react";
import { format } from "date-fns";
import { Check, ChevronLeft, Loader2, Camera } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useUnitItems } from "@/hooks/useUnitItems";
import { usePropertyRooms } from "@/hooks/usePropertyRooms";
import { useRoomCoverData } from "@/hooks/useRoomCoverPhotos";
import {
  syncUnitCheckIssueReport,
  useUnitCheckMarks,
  useUpsertUnitCheckMark,
  type UnitCheckStatus,
} from "@/hooks/useUnitCheckMarks";
import { useCreateUnitPhotoAsk, useUnitPhotoAsks } from "@/hooks/useUnitPhotoAsks";
import { useCreateMaintenanceReport } from "@/hooks/useMaintenanceReports";
import { readSimpleFlowState, type RoomShots, type RoomSkipReason } from "@/lib/simpleTurnover";
import { sortRoomsWalkOrder } from "@/lib/roomCoverPhotos";
import { normalizeRoomName } from "@/lib/roomUtils";
import { sortUnitItems } from "@/lib/unitItems";
import { MediaThumb } from "@/components/media/MediaThumb";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const STATUS_CHIPS: { id: UnitCheckStatus; label: string }[] = [
  { id: "here", label: "Here" },
  { id: "missing", label: "Missing" },
  { id: "broken", label: "Broken" },
];

const ON_COLOR: Record<UnitCheckStatus, string> = {
  here: "#24a89a",
  missing: "#b4453a",
  broken: "#8a5a00",
};

const SKIP_TEXT: Record<RoomSkipReason, string> = {
  locked: "Door is locked",
  guest: "Guest is still inside",
  unsafe: "Not safe to go in",
};

export interface LiveJobDeskProps {
  propertyId: string;
  cleaningTaskId: string;
  cleanerId: string | null;
  cleanerName: string;
  mode: "live" | "post";
  onBack: () => void;
  onGoToUnit: () => void;
}

type CheckRoom = {
  id: string;
  name: string;
  coverUrl: string | null;
  shots: RoomShots | undefined;
  items: ReturnType<typeof sortUnitItems>;
  isOther: boolean;
  noCover?: boolean;
};

export function LiveJobDesk({
  propertyId,
  cleaningTaskId,
  cleanerId,
  cleanerName,
  mode,
  onBack,
  onGoToUnit,
}: LiveJobDeskProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const live = mode === "live";

  const { data: taskRow, isLoading: taskLoading } = useQuery({
    queryKey: ["live-job-desk-task", cleaningTaskId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cleaning_tasks")
        .select("id, status, started_at, completed_at, scheduled_date, entry_walkthrough_state, cleaner_id")
        .eq("id", cleaningTaskId)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as {
        id: string;
        status: string;
        started_at: string | null;
        completed_at: string | null;
        scheduled_date: string | null;
        entry_walkthrough_state: unknown;
        cleaner_id: string | null;
      } | null;
    },
    enabled: !!cleaningTaskId,
    refetchInterval: live ? 4000 : false,
  });

  const { data: itemsRaw = [], isLoading: itemsLoading } = useUnitItems(propertyId);
  const items = useMemo(() => sortUnitItems(itemsRaw), [itemsRaw]);
  const { data: roomRows = [], isLoading: roomsLoading } = usePropertyRooms(propertyId);
  const { data: coverData = {} } = useRoomCoverData(propertyId);
  const { data: marks = [], isLoading: marksLoading } = useUnitCheckMarks(cleaningTaskId);
  const { data: asks = [] } = useUnitPhotoAsks(cleaningTaskId, { refetchInterval: live ? 4000 : false });
  const { data: propertyRow } = useQuery({
    queryKey: ["check-unit-property-name", propertyId],
    queryFn: async () => {
      const { data } = await supabase.from("properties").select("name").eq("id", propertyId).maybeSingle();
      return (data as { name?: string | null } | null)?.name ?? null;
    },
    staleTime: 60_000,
  });
  const upsert = useUpsertUnitCheckMark();
  const createAsk = useCreateUnitPhotoAsk();
  const createReport = useCreateMaintenanceReport();

  const [busyId, setBusyId] = useState<string | null>(null);
  const [cleared, setCleared] = useState<Record<string, boolean>>({});

  const simple = useMemo(() => readSimpleFlowState(taskRow?.entry_walkthrough_state), [taskRow?.entry_walkthrough_state]);
  const hasRoomTags = !!simple.walk_by_room;
  const walkComplete = !!simple.walk_complete || !!simple.before_complete;
  const firstName = (cleanerName || "").trim().split(/\s+/)[0] || "the cleaner";

  const markByItem = useMemo(() => {
    const m = new Map<string, (typeof marks)[0]>();
    for (const row of marks) m.set(row.unit_item_id, row);
    return m;
  }, [marks]);

  const sections = useMemo<CheckRoom[]>(() => {
    const live_rooms = roomRows.filter((r) => !r.archived_at);
    const ordered = sortRoomsWalkOrder(live_rooms, coverData);
    const used = new Set<string>();
    const out: CheckRoom[] = ordered.map((room) => {
      const n = normalizeRoomName(room.name);
      const roomItems = items.filter((it) => normalizeRoomName(it.room || "") === n);
      roomItems.forEach((it) => used.add(it.id));
      return {
        id: room.id,
        name: room.name,
        coverUrl: coverData[room.id]?.cover_photo_url ?? null,
        shots: simple.walk_by_room?.[room.id],
        items: roomItems,
        isOther: false,
        noCover: !!coverData[room.id]?.cover_not_needed,
      };
    });
    const rest = items.filter((it) => !used.has(it.id));
    if (rest.length > 0) {
      out.push({ id: "other", name: "Other items", coverUrl: null, shots: undefined, items: rest, isOther: true });
    }
    return out;
  }, [roomRows, coverData, items, simple.walk_by_room]);

  const roomCount = sections.filter((s) => !s.isOther).length;
  const photoTotal = useMemo(
    () =>
      hasRoomTags
        ? sections.reduce((n, s) => n + (s.shots?.photo_urls.length ?? 0), 0)
        : (simple.before_photo_urls ?? []).length,
    [sections, hasRoomTags, simple.before_photo_urls],
  );

  const total = items.length;
  const checked = items.filter((i) => markByItem.has(i.id)).length;
  const problems = marks.filter((m) => m.status === "missing" || m.status === "broken").length;
  const pct = total > 0 ? Math.round((checked / total) * 100) : 0;

  const findingsByRoom = useMemo(() => {
    const m = new Map<string, { key: string; note: string; photo: string | null }[]>();
    (simple.inspect_findings ?? []).forEach((f, i) => {
      if ((f.status !== "missing" && f.status !== "broken") || !f.room_name) return;
      const key = normalizeRoomName(f.room_name);
      const list = m.get(key) ?? [];
      list.push({
        key: `${i}-${f.evidence_photo_url ?? ""}`,
        note: f.note?.trim() || (f.status === "missing" ? "Missing" : "Broken"),
        photo: f.evidence_photo_url || null,
      });
      m.set(key, list);
    });
    return m;
  }, [simple.inspect_findings]);

  const setStatus = async (unitItemId: string, status: UnitCheckStatus) => {
    const item = items.find((i) => i.id === unitItemId);
    if (!item) return;
    const existing = markByItem.get(unitItemId);
    if (existing?.status === status) return;
    setBusyId(unitItemId);
    try {
      let issueReportId = existing?.issue_report_id ?? null;
      if ((status === "missing" || status === "broken") && !issueReportId) {
        if (cleanerId) {
          try {
            const report = await createReport.mutateAsync({
              property_id: propertyId,
              cleaning_task_id: cleaningTaskId,
              cleaner_id: cleanerId,
              room_name: item.room || "Unit",
              response: status === "broken" ? "needs_repair" : "no",
              notes: `${status === "broken" ? "Broken" : "Missing"}: ${item.name}`,
              photos: existing?.proof_photo_url ? [existing.proof_photo_url] : item.photo_url ? [item.photo_url] : [],
              task_description: status === "broken" ? `Broken: ${item.name}` : `Missing: ${item.name}`,
              checklist_name: "Unit check",
            });
            issueReportId = report.id;
          } catch {
            /* mark still saves */
          }
        }
      }

      if (existing?.issue_report_id && existing.status !== status) {
        await syncUnitCheckIssueReport({ reportId: existing.issue_report_id, status, itemName: item.name });
      }

      await upsert.mutateAsync({
        cleaningTaskId,
        unitItemId,
        status,
        issueReportId: issueReportId ?? undefined,
        proofPhotoUrl: existing?.proof_photo_url,
      });

      if (status === "here" && (existing?.status === "missing" || existing?.status === "broken")) {
        setCleared((c) => ({ ...c, [unitItemId]: true }));
        window.setTimeout(() => setCleared((c) => {
          const next = { ...c };
          delete next[unitItemId];
          return next;
        }), 2600);
      } else {
        setCleared((c) => {
          if (!c[unitItemId]) return c;
          const next = { ...c };
          delete next[unitItemId];
          return next;
        });
      }
      void queryClient.invalidateQueries({ queryKey: ["maintenance-reports", propertyId] });
      void queryClient.invalidateQueries({ queryKey: ["maintenance-reports-for-task", cleaningTaskId] });
      void queryClient.invalidateQueries({ queryKey: ["reports-unit-check-counts", propertyId] });
      void queryClient.invalidateQueries({ queryKey: ["reports-unit-check-marks", propertyId] });
    } catch (err) {
      toast({
        title: "Couldn’t save",
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Ask the cleaner for photos of a room. Photo asks are kept per unit item, so the ask goes on the
   * room's first item; the cleaner photographs it and the photo lands in this room.
   */
  const askForRoomPhotos = async (room: CheckRoom) => {
    const first = room.items[0];
    if (!live || !first) return;
    setBusyId(`ask-${room.id}`);
    try {
      await createAsk.mutateAsync({ cleaningTaskId, unitItemId: first.id, requestedBy: user?.id ?? null });
    } catch (err) {
      toast({
        title: "Couldn’t ask",
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusyId(null);
    }
  };

  const jumpTo = (id: string) => {
    document.getElementById(`check-room-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (taskLoading || itemsLoading || marksLoading || roomsLoading) {
    return (
      <div className="flex justify-center py-16 text-[#64748b]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col gap-6">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex w-fit items-center gap-2 text-[13px] font-medium text-[#64748b] hover:text-[#24a89a]"
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={2.4} />
          Back
        </button>
        <div className="flex flex-col items-center gap-3 rounded-[18px] border-2 border-dashed border-[#e6edf1] bg-white px-6 py-16">
          <div className="text-[22px] font-bold text-[#0f1720]">Set up Unit first</div>
          <div className="text-[15px] font-medium text-[#64748b]">Add baseline items on the Unit tab</div>
          <button
            type="button"
            onClick={onGoToUnit}
            className="mt-2 flex h-12 items-center rounded-xl bg-[#24a89a] px-6 text-[15px] font-bold text-white"
          >
            Go to Unit
          </button>
        </div>
      </div>
    );
  }

  const cleanDate = taskRow?.completed_at ?? taskRow?.started_at ?? taskRow?.scheduled_date ?? null;
  const subline = [
    cleanDate ? `Clean on ${format(new Date(cleanDate.length === 10 ? `${cleanDate}T12:00:00` : cleanDate), "EEE d MMM")}` : null,
    cleanerName || null,
    roomCount > 0 ? `${roomCount} room${roomCount === 1 ? "" : "s"}` : null,
    `${photoTotal} walk photo${photoTotal === 1 ? "" : "s"}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="flex flex-col gap-5 pb-10" data-tour="tour-live-job-desk">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex w-fit items-center gap-2 text-[13px] font-medium text-[#64748b] hover:text-[#24a89a]"
      >
        <ChevronLeft className="h-4 w-4" strokeWidth={2.4} />
        {live ? "Schedule" : "Reports"}
      </button>

      <div className="sticky top-0 z-20 -mx-1 flex flex-col gap-3.5 rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white px-5 pb-3.5 pt-4">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            {propertyRow ? <div className="text-[13.5px] font-bold text-[#64748b]">{propertyRow}</div> : null}
            <div className="flex flex-wrap items-center gap-3">
              <div className="text-[28px] font-bold tracking-[-0.6px] text-[#0f1720] max-sm:text-[22px]">Check unit</div>
              <span
                className={cn(
                  "whitespace-nowrap rounded-full px-3 py-[5px] text-[12.5px] font-bold",
                  live ? "bg-[#e2eeed] text-[#15776d]" : "bg-[#eef2f5] text-[#64748b]",
                )}
              >
                {live ? "Job live" : "Job finished"}
              </span>
            </div>
            <div className="text-[14px] font-medium text-[#475569]">{subline}</div>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-[300px]">
            <div className="whitespace-nowrap text-[24px] font-bold tracking-[-0.4px] max-sm:text-[20px]">
              {checked} of {total} checked
            </div>
            <div className="h-2.5 overflow-hidden rounded-full bg-[#e6edf1]">
              <div className="h-full rounded-full bg-[#24a89a]" style={{ width: `${pct}%` }} />
            </div>
            {problems > 0 ? (
              <div className="whitespace-nowrap text-[14px] font-bold text-[#b4453a]">
                {problems} {problems === 1 ? "problem" : "problems"} in the report
              </div>
            ) : null}
          </div>
        </div>
        <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-0.5">
          {sections.map((room) => {
            const done = room.items.filter((i) => markByItem.has(i.id)).length;
            const probs = room.items.filter((i) => {
              const s = markByItem.get(i.id)?.status;
              return s === "missing" || s === "broken";
            }).length;
            const all = room.items.length > 0 && done === room.items.length;
            return (
              <button
                key={room.id}
                type="button"
                onClick={() => jumpTo(room.id)}
                className={cn(
                  "flex h-[38px] shrink-0 items-center gap-2 whitespace-nowrap rounded-full border-[1.5px] px-3 text-[14px] font-bold hover:border-[#24a89a]",
                  all ? "border-[#e2eeed] bg-[#e2eeed] text-[#15776d]" : "border-[#d7e0e6] bg-white text-[#0f1720]",
                )}
              >
                {room.name}
                <span className={cn("text-[13px]", probs ? "text-[#b4453a]" : all ? "text-[#15776d]" : "text-[#8494a2]")}>
                  {done}/{room.items.length}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-[22px]">
        {sections.map((room, ri) => {
          const done = room.items.filter((i) => markByItem.has(i.id)).length;
          const probs = room.items.filter((i) => {
            const s = markByItem.get(i.id)?.status;
            return s === "missing" || s === "broken";
          }).length;
          const allDone = room.items.length > 0 && done === room.items.length;
          const photos = room.shots?.photo_urls ?? [];
          const skip = room.shots?.skip;
          const noPhotos = hasRoomTags && !room.isOther && !skip && photos.length === 0 && (walkComplete || !live);
          const waiting = hasRoomTags && !room.isOther && !skip && photos.length === 0 && !noPhotos;
          const roomAsk = asks.find((a) => room.items.some((it) => it.id === a.unit_item_id) && a.status === "open");
          const flagged = findingsByRoom.get(normalizeRoomName(room.name)) ?? [];
          const skippedAt = (room.shots as (RoomShots & { skipped_at?: string }) | undefined)?.skipped_at;
          return (
            <div
              key={room.id}
              id={`check-room-${room.id}`}
              className="flex scroll-mt-[220px] flex-col gap-[18px] rounded-[20px] border-[1.5px] border-[#e6edf1] bg-white p-5 max-sm:p-3.5"
            >
              <div className="flex items-center gap-[18px] max-sm:flex-col max-sm:items-stretch">
                {!room.isOther && !room.noCover ? (
                  <div className="relative h-[128px] w-[196px] shrink-0 overflow-hidden rounded-[14px] bg-[repeating-linear-gradient(135deg,#e6edf1_0_10px,#eff4f7_10px_20px)] max-sm:h-[168px] max-sm:w-full">
                    {room.coverUrl ? <img src={room.coverUrl} alt="" className="h-full w-full object-cover" /> : null}
                    {room.coverUrl ? (
                      <span className="absolute bottom-2 left-2 rounded-full bg-[rgba(15,23,32,0.72)] px-2.5 py-1 text-[11.5px] font-bold text-white">
                        Cover photo
                      </span>
                    ) : null}
                  </div>
                ) : null}
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  {!room.isOther ? (
                    <div className="text-[13.5px] font-bold text-[#15776d]">
                      Room {ri + 1} of {roomCount}
                    </div>
                  ) : null}
                  <div className="flex items-center gap-2.5">
                    <div className="text-[26px] font-bold tracking-[-0.5px] max-sm:text-[21px] max-sm:leading-tight">
                      {room.name}: {done} of {room.items.length} checked
                    </div>
                    {allDone ? (
                      <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-[#24a89a]">
                        <Check className="h-3.5 w-3.5 text-white" strokeWidth={3.2} />
                      </span>
                    ) : null}
                  </div>
                  <div className="flex gap-3.5 text-[14px] font-bold">
                    {hasRoomTags && !room.isOther ? (
                      <span className="text-[#64748b]">{photos.length ? `${photos.length} walk photos` : "No walk photos"}</span>
                    ) : null}
                    {probs > 0 ? (
                      <span className="text-[#b4453a]">
                        {probs} {probs === 1 ? "problem" : "problems"}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>

              {photos.length > 0 ? (
                <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-6 sm:gap-2.5">
                  {photos.map((url) => (
                    <div key={url} className="aspect-square overflow-hidden rounded-[12px] sm:aspect-[4/3]">
                      <MediaThumb url={url} />
                    </div>
                  ))}
                </div>
              ) : null}

              {flagged.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {flagged.map((p) => (
                    <div key={p.key} className="flex items-center gap-3 rounded-[14px] border-[1.5px] border-[#f0d3d0] bg-white px-3 py-2.5">
                      <div className="h-11 w-11 shrink-0 overflow-hidden rounded-[10px] bg-[#eff4f7]">
                        {p.photo ? <MediaThumb url={p.photo} /> : null}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[14.5px] font-bold text-[#0f1720]">{p.note}</div>
                        <div className="text-[12px] font-bold text-[#b4453c]">Flagged by {firstName}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              {skip ? (
                <div className="flex flex-col gap-0.5 rounded-2xl border-2 border-[#e8b45c] bg-[#fdf3e3] px-[18px] py-4">
                  <div className="text-[18px] font-bold">Skipped · {SKIP_TEXT[skip]}</div>
                  <div className="text-[14px] font-medium leading-snug text-[#8a5a00]">
                    {firstName} skipped this room{skippedAt ? ` at ${format(new Date(skippedAt), "HH:mm")}` : ""}. No photos from
                    this room. Check these items yourself next time you’re in.
                  </div>
                </div>
              ) : null}

              {waiting ? (
                <div className="rounded-[14px] border-[1.5px] border-dashed border-[#e6edf1] bg-[#f9fbfc] px-4 py-5 text-center text-[14px] font-medium text-[#8494a2]">
                  Waiting for photos…
                </div>
              ) : null}

              {noPhotos ? (
                <div className="flex flex-wrap items-center gap-3.5 rounded-2xl border-2 border-[#e8b45c] bg-[#fdf3e3] px-[18px] py-4">
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <div className="text-[18px] font-bold">No photos from this room</div>
                    <div className="text-[14px] font-medium leading-snug text-[#8a5a00]">
                      {roomAsk
                        ? `Asked ${firstName} at ${format(new Date(roomAsk.created_at), "HH:mm")}. The photos will show here when ${firstName} sends them.`
                        : live
                          ? `${firstName} finished the walk without taking photos here. Check these items yourself, or ask ${firstName} for photos.`
                          : `${firstName} finished without taking photos here. The job is closed, so you can’t ask for photos now.`}
                    </div>
                  </div>
                  {live && !roomAsk && room.items.length > 0 ? (
                    <button
                      type="button"
                      disabled={busyId === `ask-${room.id}`}
                      onClick={() => void askForRoomPhotos(room)}
                      className="flex h-[52px] shrink-0 items-center gap-2 rounded-[14px] bg-[#24a89a] px-[22px] text-[16px] font-bold text-white hover:bg-[#1b8378] max-sm:w-full max-sm:justify-center"
                    >
                      <Camera className="h-[18px] w-[18px]" strokeWidth={2.2} />
                      Ask {firstName} for photos
                    </button>
                  ) : null}
                </div>
              ) : null}

              <div className="flex flex-col overflow-hidden rounded-2xl border border-[#e6edf1]">
                {room.items.length === 0 ? (
                  <div className="px-4 py-5 text-[14px] font-medium text-[#8494a2]">No unit items in this room.</div>
                ) : null}
                {room.items.map((item) => {
                  const status = markByItem.get(item.id)?.status ?? null;
                  const isProb = status === "missing" || status === "broken";
                  const tint = status === "missing" ? "bg-[#fdf1ef]" : status === "broken" ? "bg-[#fdf3e3]" : "bg-white";
                  let sub = "Not checked yet";
                  let subColor = "#8494a2";
                  if (cleared[item.id]) {
                    sub = "Problem cleared";
                    subColor = "#15776d";
                  } else if (status === "missing") {
                    sub = "Missing · in the report";
                    subColor = "#b4453a";
                  } else if (status === "broken") {
                    sub = "Broken · in the report";
                    subColor = "#8a5a00";
                  } else if (status === "here") {
                    sub = "";
                  }
                  const rowBusy = busyId === item.id;
                  return (
                    <div
                      key={item.id}
                      className={cn(
                        "flex items-center gap-4 border-b border-[#eef2f5] px-3.5 py-3 last:border-b-0 max-sm:flex-col max-sm:items-stretch max-sm:gap-2.5",
                        tint,
                        isProb && "max-sm:rounded-[14px]",
                      )}
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
                        <div className="h-[68px] w-[68px] shrink-0 overflow-hidden rounded-xl border border-[#e6edf1] bg-[repeating-linear-gradient(135deg,#e6edf1_0_6px,#eff4f7_6px_12px)] max-sm:h-[52px] max-sm:w-[52px]">
                          {item.photo_url ? <img src={item.photo_url} alt="" className="h-full w-full object-cover" /> : null}
                        </div>
                        <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
                          <div className="text-[17px] font-bold max-sm:text-[16px]">{item.name}</div>
                          {sub ? (
                            <div className="text-[13.5px] font-bold" style={{ color: subColor }}>
                              {sub}
                            </div>
                          ) : null}
                        </div>
                        {rowBusy ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[#24a89a]" /> : null}
                      </div>
                      <div className="flex shrink-0 gap-2 max-sm:gap-1.5">
                        {STATUS_CHIPS.map((chip) => {
                          const on = status === chip.id;
                          return (
                            <button
                              key={chip.id}
                              type="button"
                              disabled={rowBusy}
                              onClick={() => void setStatus(item.id, chip.id)}
                              style={on ? { background: ON_COLOR[chip.id], borderColor: ON_COLOR[chip.id], color: "#ffffff" } : undefined}
                              className={cn(
                                "flex h-14 w-[118px] items-center justify-center rounded-[14px] border-[1.5px] text-[16px] font-bold max-sm:h-[52px] max-sm:w-auto max-sm:flex-1",
                                !on && "border-[#d7e0e6] bg-white text-[#0f1720]",
                              )}
                            >
                              {chip.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
